import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0109 from "../lesson-09/lesson";
const linkLesson0109 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0109.id);

const theory = `From this chapter symbol subscript
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>c</mi>
</math>
which indicates that the quantities are complex no longer appears: in the frequency domain all quantities are complex a priori.
<p>When a wave propagates in a medium (e.g. in air) and encounter another medium (e.g. a wall), part of the energy is reflected. So when two plane waves interfere it is common that one of the two is the consequence of the reflection of the first.
<h3 class=sectionHead>Reflection coefficient</h3>
<p>
   We noticed that when two waves propagate in two different directions, one of them could be originating from reflection. The ratio of the complex amplitude of two sine waves at the obstacle is defined as the reflection coefficient. This ratio is characteristic of the obstacle and is generally a complex function of frequency. We can write the reflection coefficient for pressure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   for an obstacle located at x = 0 is given by
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>x</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>x</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
          </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   are the incident and reflected waves (see <a href="#" data-img-alt="Figure 1. Incident and reflected waves on both side of a wall." data-img-src=${config.ASSETS_URL}/01/05/theory/figure-1.svg>Figure 1</a>), respectively. Depending on the situation, the incident wave can come from the left (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
   </math>
   ), or from the right (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
   </math>
   ).
   <figure class=figure>
<p><img alt="Figure 1. Incident and reflected waves on both side of a wall." src=${config.ASSETS_URL}/01/05/theory/figure-1.svg><figcaption class=caption>Figure 1. Incident and reflected waves on both side of a wall.</figcaption></figure>
<p>The reflection coefficient being complex we can write
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-rel>|</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>|</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jϕ</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>|</mo>
   </math>
   represents the ratio of the amplitudes (in module) and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ϕ</mi>
   </math>
   represents the reflection phase shift which may for example be due to the fact that the wave penetrates into the obstacle before it is reflected.
<p>A reflection coefficient can also be defined for the particle velocity
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>v</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>x</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>x</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   Since
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>±</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>∓</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>
   , we have in any case
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>v</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(4)
</table>
<h3 class=sectionHead>Important special cases</h3>
<h4 class=subsectionHead>Total reflection without change of sign for the pressure</h4>
<p>
   As seen before, this is the case of a perfectly reflecting rigid wall. In this case we have
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
     <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   and, since
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Z</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mfrac>
         <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   (see <a href="${linkLesson0109}" target="_blank">Lesson 1.9</a>),
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Z</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>+</mo>
      <mi>∞</mi>
   </math>.
<h4 class=subsectionHead>Total reflection with change of sign for the pressure</h4>
<p>
   As seen before, this is the case of an open tube at low frequencies. In this case we have
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
   </math>
   then
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Z</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>.
<h4 class=subsectionHead>No reflection</h4>
<p>
   This is the case where there is no obstacle. In this case the reflection coefficient is zero,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   then
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Z</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>. So, if the impedance of the obstacle is equal to the characteristic impedance, there is no reflection. It is said that the impedance is matched. This result is used in the case of some anechoic terminations.
<h3 class=sectionHead>Standing Wave Ratio</h3>
<p>When a harmonic wave is totally reflected by an obstacle, we saw that it gives rise to standing waves. When the reflection is not complete, a superposition of a traveling wave and standing wave occurs. Indeed, considering A and B real, it can be written
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mi>B</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-open>(</mo>
            <mi>B</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>A</mi>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mn>2</mn>
            <mi>A</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi mathvariant=italic>kx</mi>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   Compared to a pure stationary wave there are always nodes and antinodes but nodes do not correspond to a zero amplitude but simply to a minimum amplitude (see <a href="#" data-img-alt="Progressive waves to the right (A), to the left (B) and standing wave (A + B pressure and A-B particle velocity) at different time." data-img-src=${config.ASSETS_URL}/01/05/theory/figure-2.svg>Figure 2</a>).
   <figure class=figure>
<p><img alt="Figure 2. Progressive waves to the right (A), to the left (B) and standing wave (A + B pressure and A-B particle velocity) at different time." src=${config.ASSETS_URL}/01/05/theory/figure-2.svg><figcaption class=caption>Figure 2. Progressive waves to the right (A), to the left (B) and standing wave (A + B pressure and A-B particle velocity) at different time.</figcaption></figure>
<p>
   The Standing Wave Ratio (SWR) is defined as the ratio of the maximum amplitude
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msub>
   </math>
   to the minimum amplitude
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>min</mi>
         </mrow>
      </msub>
   </math>
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi mathvariant=italic>SWR</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>max</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>min</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   For a pure standing wave one has
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>min</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   then
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>SWR</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>+</mo>
      <mi>∞</mi>
   </math>
   , whereas for a progressive wave one has
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>min</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msub>
   </math>, then
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>SWR</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>. So
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
      <mo class=MathClass-rel><</mo>
      <mi mathvariant=italic>SWR</mi>
      <mo class=MathClass-rel><</mo>
      <mi>∞</mi>
   </math>. Note that another definition exists:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>SWR</mi>
      <mo class=MathClass-bin>∗</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>min</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>max</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. In that case
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>0</mn>
      <mo class=MathClass-rel><</mo>
      <mi mathvariant=italic>SWR</mi>
      <mo class=MathClass-bin>∗</mo>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>
   and so there is no ambiguity between both definitions.
<h3 class=sectionHead>SWR and reflection coefficient</h3>
<p>If we consider that one of the two waves results from the reflection at the obstacle, we can write
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>p</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-bin>+</mo>
            <mi>B</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkx</mi>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <mo class=MathClass-rel>|</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>|</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mn>2</mn>
                  <mi mathvariant=italic>kx</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>ϕ</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   The maximum is
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-rel>|</mo>
      <mi>A</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>+</mo>
      <mo class=MathClass-rel>|</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>|</mo>
   </math>
   and is obtained for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi>j</mi>
            <mo class=MathClass-open>(</mo>
            <mn>2</mn>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   while the minimum is
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-rel>|</mo>
      <mi>A</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mo class=MathClass-rel>|</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>|</mo>
   </math>
   and is obtained for
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi>j</mi>
            <mo class=MathClass-open>(</mo>
            <mn>2</mn>
            <mi mathvariant=italic>kx</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
   </math>. 
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mrow><mi mathvariant=italic>SWR</mi></mrow>
    </math> is then given by
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi mathvariant=italic>SWR</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mo class=MathClass-rel>|</mo>
                  <mi mathvariant=italic>Rp</mi>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mo class=MathClass-rel>|</mo>
                  <mi mathvariant=italic>Rp</mi>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(8)
</table>
<p>We also have
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mo class=MathClass-rel>|</mo>
            <mi mathvariant=italic>Rp</mi>
            <mo class=MathClass-rel>|</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>SWR</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>SWR</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mn>1</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<h3 class=sectionHead>References</h3>
  <dl class=thebibliography>
    <dt>[1]
    <dd>Leo J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
    <dt>[2]
    <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
    <dt>[3]
    <dd>Samuel Temkin. Elements of Acoustics. Wiley, New York, 1981.
  </dl>`;

export default theory;
